import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, QrCode, Trash2, Zap } from "lucide-react";
import { PageTitle, Table, Td, fmtDate } from "../../components/admin/AdminUi";
import { inr } from "../../components/admin/pricing/PricingUi";
import { CreditTracker, EmergentBalance } from "../../components/admin/credits/CreditTracker";
import AddCreditDialog from "../../components/admin/credits/AddCreditDialog";
import TopupDialog from "../../components/admin/credits/TopupDialog";
import AlertSettings from "../../components/admin/credits/AlertSettings";
import PaymentLinkDialog from "../../components/admin/credits/PaymentLinkDialog";
import PaymentLinksTable from "../../components/admin/credits/PaymentLinksTable";
import PanelBalance from "../../components/admin/credits/PanelBalance";
import PanelTopupDialog from "../../components/admin/credits/PanelTopupDialog";
import { BALANCE_EVENT } from "../../components/admin/credits/RechargeBanner";
import { api, errMsg } from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const METHOD = { upi: "UPI", bank: "Bank transfer", cash: "Cash", card: "Card", other: "Other" };

const Payments = ({ rows }) => (
  <Table head={["Merchant", "Credits", "Received", "Method", "Reference", "Added by", "Date"]} testid="manual-payments-table" empty={rows.length === 0 && "No direct payments recorded yet."}>
    {rows.map((o) => (
      <tr key={o.id} data-testid={`manual-payment-row-${o.id}`} className="hover:bg-slate-50">
        <Td className="text-slate-700">{o.user_email}</Td><Td className="font-mono">{o.credits}</Td>
        <Td className="font-mono text-emerald-700">{inr(o.amount_paise / 100)}</Td><Td className="text-xs">{METHOD[o.method] || o.method}</Td>
        <Td className="text-xs font-mono text-slate-500">{o.reference || "—"}</Td><Td className="text-xs text-slate-500">{o.added_by}</Td>
        <Td className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(o.created_at)}</Td>
      </tr>
    ))}
  </Table>
);

const Topups = ({ rows, onDelete }) => {
  const [del, setDel] = useState(null);
  return (
    <>
      <Table head={["Date", "Credits", "You paid", "Note", ""]} testid="topups-table" empty={rows.length === 0 && "No Emergent top-ups recorded yet."}>
        {rows.map((t) => (
          <tr key={t.id} data-testid={`topup-row-${t.id}`} className="hover:bg-slate-50">
            <Td className="text-xs whitespace-nowrap">{t.paid_on}</Td><Td className="font-mono">{t.credits.toLocaleString("en-IN")}</Td>
            <Td className="font-mono">{inr(t.amount_inr)}</Td><Td className="text-xs text-slate-500">{t.note || "—"}</Td>
            <Td className="text-right"><button onClick={() => setDel(t)} data-testid={`topup-delete-${t.id}`} className="text-slate-400 hover:text-rose-600" aria-label="Delete top-up"><Trash2 size={14} /></button></Td>
          </tr>
        ))}
      </Table>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-white" data-testid="topup-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this top-up?</AlertDialogTitle>
            <AlertDialogDescription>{del && `${del.credits.toLocaleString("en-IN")} credits from ${del.paid_on} will be removed from your Emergent balance estimate.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="topup-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(del)} data-testid="topup-delete-confirm" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default function AdminCredits() {
  const [d, setD] = useState(null);
  const [links, setLinks] = useState([]);
  const [panel, setPanel] = useState(null);
  const [shown, setShown] = useState(null);
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState(params.get("topup") ? "topup" : params.get("panel") ? "panel" : null);
  const loadLinks = useCallback(() => {
    api.adminPanel().then(setPanel).catch(() => {});
    return api.adminPaymentLinks().then(setLinks).catch(() => {});
  }, []);
  const load = useCallback(() => { loadLinks(); return api.adminCreditsOverview().then(setD).catch((e) => toast.error(errMsg(e))); }, [loadLinks]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const want = params.get("topup") ? "topup" : params.get("panel") ? "panel" : null;
    if (want) { setDialog(want); setParams({}, { replace: true }); }
  }, [params, setParams]);
  const refresh = () => { load(); window.dispatchEvent(new Event(BALANCE_EVENT)); };
  const del = async (t) => {
    try { await api.adminDeleteTopup(t.id); toast.success("Top-up deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <div className="grid sm:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 rounded-2xl shimmer" />)}</div>;
  return (
    <div data-testid="admin-credits" className="space-y-8">
      <PageTitle title="Credits" desc="Charge merchants with a real payment link or QR, add credit for direct payments, and track every credit sold, used and still left." right={
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setShown(null); setDialog("link"); }} data-testid="admin-request-payment-btn" className="h-10 px-5 rounded-full bg-sky-500 text-white text-sm font-semibold flex items-center gap-2 hover:bg-sky-600 transition-colors">
            <QrCode size={15} /> Request payment
          </button>
          <button onClick={() => setDialog("add")} data-testid="admin-add-credit-btn" className="h-10 px-5 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center gap-2 hover:bg-slate-700 transition-colors">
            <Zap size={15} className="fill-amber-400 text-amber-400" /> Add credit
          </button>
        </div>} />
      {panel && <PanelBalance p={panel} onTopup={() => setDialog("panel")} />}
      <CreditTracker d={d} />
      <EmergentBalance e={d.emergent} right={
        <button onClick={() => setDialog("topup")} data-testid="admin-record-topup-btn" className="h-9 px-4 rounded-full border border-slate-300 text-sm text-slate-700 flex items-center gap-1.5 hover:bg-slate-100"><Plus size={14} /> Record recharge</button>} />
      <AlertSettings e={d.emergent} onSaved={load} />
      <section className="space-y-3"><h2 className="text-lg font-semibold text-slate-900">Payment links</h2>
        <PaymentLinksTable rows={links} onShow={(l) => { setShown(l); setDialog("link"); }} onChange={load} /></section>
      <section className="space-y-3"><h2 className="text-lg font-semibold text-slate-900">Direct payments from merchants</h2><Payments rows={d.manual_payments} /></section>
      <section className="space-y-3"><h2 className="text-lg font-semibold text-slate-900">Your Emergent top-ups</h2><Topups rows={d.topups} onDelete={del} /></section>
      <PaymentLinkDialog open={dialog === "link"} link={shown} onClose={() => setDialog(null)} onChange={load} />
      <PanelTopupDialog open={dialog === "panel"} price={panel?.price_per_credit_inr || 0} onClose={() => setDialog(null)} onDone={load} />
      <AddCreditDialog open={dialog === "add"} onClose={() => setDialog(null)} onDone={load} />
      <TopupDialog open={dialog === "topup"} base={d.base_cost_inr} onClose={() => setDialog(null)} onDone={refresh} />
    </div>
  );
}
